"use client";

import { create } from "zustand";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";

// Define interfaces for our data models
interface Product {
  id: number;
  name: string;
  price: number;
  slug: string;
  description?: string;
  image_url: string;
  category_id?: number;
}

interface WishlistItem {
  id: number;
  product_id: number;
  user_id: string;
  created_at: string;
  products: Product;
}

interface WishlistStore {
  items: Product[];
  isLoading: boolean;
  init: () => Promise<void>;
  addItem: (product: Product) => Promise<void>;
  removeItem: (productId: number) => Promise<void>;
  isInWishlist: (productId: number) => boolean;
}

export const useWishlist = create<WishlistStore>((set, get) => ({
  items: [],
  isLoading: false,
  
  init: async () => {
    set({ isLoading: true });
    
    try {
      const supabase = await createClient();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        set({ items: [], isLoading: false });
        return;
      }

      console.log('Fetching wishlist for user:', user.id);
      
      const { data, error } = await supabase
        .from('wishlist')
        .select(`
          id,
          product_id,
          products!wishlist_product_id_fkey(
            id,
            name,
            price,
            slug,
            description,
            image_url,
            category_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching wishlist:', error);
        throw error;
      }
      
      // Type assertion for the response data
      const wishlistItems = data as unknown as WishlistItem[];
      
      const products = wishlistItems
        .filter(item => item.products)
        .map(item => ({
          ...item.products,
          id: Number(item.products.id) // Ensure ID is a number
        }));

      set({ items: products, isLoading: false });
      
    } catch (error) {
      console.error('Error in wishlist init:', error);
      toast.error('Failed to load your wishlist');
      set({ isLoading: false });
    }
  },
  
  addItem: async (product: Product) => {
    try {
      const supabase = await createClient();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        toast.error('Please sign in to add items to your wishlist');
        return;
      }
      
      // Check if item is already in wishlist in the database
      const { data: existingItem, error: checkError } = await supabase
        .from('wishlist')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', product.id)
        .single();
  
      if (existingItem) {
        toast.info('This item is already in your wishlist');
        return;
      }
  
      const { error } = await supabase
        .from('wishlist')
        .insert({
          user_id: user.id,
          product_id: product.id,
          created_at: new Date().toISOString()
        });
  
      if (error) throw error;
      
      // Update local state after successful database operation
      set(state => ({
        items: [product, ...state.items]
      }));
      
      toast.success('Added to wishlist');
      
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast.error('Failed to add item to wishlist');
    }
  },
  
  removeItem: async (productId: number) => {
    try {
      const supabase = await createClient();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        toast.error('Please sign in to modify your wishlist');
        return;
      }
      
      const { error } = await supabase
        .from('wishlist')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId);
      
      if (error) throw error;
      
      // Update local state
      set(state => ({
        items: state.items.filter(item => item.id !== productId)
      }));
      
      toast.success('Removed from wishlist');
      
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error('Failed to remove item from wishlist');
    }
  },
  
  isInWishlist: (productId: number) => {
    return get().items.some(item => item.id === productId);
  }
}));